import { useState } from "react";
import { Notebook, Download, Trash2, X } from "@/lib/icons";
import { useLabNotebook, type NotebookEntry } from "@/lib/labNotebook";
import { exportNotebookPdf } from "@/lib/labNotebookPdf";

const stationLabel: Record<NotebookEntry["station"], string> = {
  mixing: "Mixing",
  ph: "pH",
  flame: "Flame",
  titration: "Titration",
};

const stationStyle: Record<NotebookEntry["station"], string> = {
  mixing: "bg-primary/10 text-primary border-primary/20",
  ph: "bg-blue-500/10 text-blue-400 border-blue-400/20",
  flame: "bg-orange-500/10 text-orange-400 border-orange-400/20",
  titration: "bg-emerald-500/10 text-emerald-400 border-emerald-400/20",
};

export default function LabNotebook() {
  const { entries, removeEntry, clear } = useLabNotebook();
  const [studentName, setStudentName] = useState("");

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
              <Notebook size={16} className="text-primary" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Lab Notebook</h2>
              <p className="text-xs text-muted-foreground">
                {entries.length === 0
                  ? "Experiments are auto-logged here as you work."
                  : `${entries.length} experiment${entries.length === 1 ? "" : "s"} recorded`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="notebook-student" className="sr-only">Student name</label>
            <input
              id="notebook-student"
              type="text"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              placeholder="Student name (optional)"
              className="h-8 w-44 rounded-md border border-input bg-background px-2.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              onClick={() => exportNotebookPdf(entries, studentName.trim() || undefined)}
              disabled={entries.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Export lab notebook to PDF"
            >
              <Download size={12} aria-hidden="true" />
              Export PDF
            </button>
            <button
              onClick={() => {
                if (entries.length === 0) return;
                if (confirm("Clear all notebook entries? This cannot be undone.")) clear();
              }}
              disabled={entries.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Clear all notebook entries"
            >
              <Trash2 size={12} aria-hidden="true" />
              Clear
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            <Notebook size={32} className="mx-auto mb-2 opacity-30" aria-hidden="true" />
            <p>Run an experiment in the Mixing, pH, or Titration tab - entries appear here automatically.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {entries.map((entry) => (
              <li key={entry.id} className="py-3 flex items-start gap-3">
                <span
                  className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${stationStyle[entry.station]}`}
                >
                  {stationLabel[entry.station]}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{entry.title}</p>
                  {entry.equation && (
                    <p className="text-xs font-mono text-muted-foreground mt-0.5 truncate">
                      {entry.equation}
                    </p>
                  )}
                  {entry.observation && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{entry.observation}</p>
                  )}
                  {entry.pH !== undefined && (
                    <p className="text-xs text-muted-foreground mt-0.5">pH {entry.pH.toFixed(2)}</p>
                  )}
                  {entry.titrationData && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Equivalence at {entry.titrationData.equivalenceVolume.toFixed(2)} mL ·
                      final pH {entry.titrationData.finalPH.toFixed(2)}
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(entry.timestamp).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => removeEntry(entry.id)}
                  className="flex-shrink-0 p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  aria-label={`Delete entry: ${entry.title}`}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
